/**
 * UI Styles for Buzztime Bingo
 * This file contains reusable UI components and styles for the Buzztime Bingo apps
 */

import React, { useEffect } from 'react';

/**
 * Button styles that can be used across all apps in the monorepo
 * These styles are designed to work well on dark backgrounds
 */


/**
 * React Button component that uses the buttonStyles
 * @param {Object} props - Button props
 * @param {string} [props.variant='primaryPink'] - Button style variant
 * @param {boolean} [props.disabled=false] - Is button disabled
 * @param {string} props.children - Button text/content
 * @param {function} props.onClick - Click handler
 * @param {string} [props.className] - Additional CSS class
 * @param {Object} [props.style] - Additional inline styles
 */
export const createStyledButton = (props) => {
  const {
    variant = 'primaryPink',
    disabled = false,
    children,
    onClick,
    className = '',
    style = {},
    ...rest
  } = props;
  
  // Return a function that creates the button element (works in both React and vanilla JS)
  return {
    // CSS classnames to use for button styling with CSS approach
    classNames: `btn btn-${variant} ${disabled ? 'btn-disabled' : ''} ${className}`,
    
    // Inline styles for direct application
    styles: {},
    
    // CSS string that can be injected into a style tag
    cssString: `
      /* Button Styles with 3D Pushable Effect */
      .btn {
        position: relative;
        display: inline-block;
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        outline-offset: 4px;
        transition: filter 250ms;
        user-select: none;
        touch-action: manipulation;
        width: 100%;
        max-width: min(400px, 90vw);
        margin: 10px 0;
        font-family: 'chicago', sans-serif;
      }
      
      .btn-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      /* Each button needs a front and shadow span element inside */
      .btn .shadow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        will-change: transform;
        transform: translateY(2px);
        transition: transform 600ms cubic-bezier(.3, .7, .4, 1);
      }
      
      .btn .front {
        display: block;
        position: relative;
        padding: clamp(0.8rem, 3vw, 1.2rem) clamp(2rem, 6vw, 4rem);
        border-radius: 10px;
        font-weight: bold;
        font-size: clamp(1rem, 4vw, 1.5rem);
        text-align: center;
        color: white;
        will-change: transform;
        transform: translateY(-6px);
        transition: transform 600ms cubic-bezier(.3, .7, .4, 1);
      }
      
      /* Hover State */
      .btn:hover:not(.btn-disabled) {
        filter: brightness(110%);
      }
      
      .btn:hover:not(.btn-disabled) .front {
        transform: translateY(-7px);
        transition: transform 250ms cubic-bezier(.3, .7, .4, 1.5);
      }
      
      .btn:hover:not(.btn-disabled) .shadow {
        transform: translateY(3px);
        transition: transform 250ms cubic-bezier(.3, .7, .4, 1.5);
      }
      
      /* Active State */
      .btn:active:not(.btn-disabled) .front {
        transform: translateY(-2px);
        transition: transform 34ms;
      }
      
      .btn:active:not(.btn-disabled) .shadow {
        transform: translateY(1px);
        transition: transform 34ms;
      }
      
      /* Button Variants */
      /* Primary Pink */
      .btn-primaryPink .shadow {
        background: #c71585;
      }
      
      .btn-primaryPink .front {
        background: linear-gradient(180deg, #ff69b4 0%, #ff1493 100%);
        box-shadow: 0 0 15px rgba(255, 105, 180, 0.5);
      }
      
      /* Primary Cyan */
      .btn-primaryCyan .shadow {
        background: #008b8b;
      }
      
      .btn-primaryCyan .front {
        background: linear-gradient(180deg, #00ffff 0%, #00cccc 100%);
        box-shadow: 0 0 15px rgba(0, 255, 255, 0.5);
      }
      
      /* Secondary */
      .btn-secondary .shadow {
        background: rgba(255, 0, 195, 0.5);
      }
      
      .btn-secondary .front {
        background-color: transparent;
        color: #FFFFFF;
        border: 2px solid #FF00C3;
        box-shadow: 0 0 10px rgba(255, 0, 195, 0.2);
      }
      
      /* Exit/Danger */
      .btn-exit .shadow,
      .btn-danger .shadow {
        background: #c10000;
      }
      
      .btn-exit .front,
      .btn-danger .front {
        background: linear-gradient(180deg, #ff3b30 0%, #ff0000 100%);
        box-shadow: 0 0 15px rgba(255, 59, 48, 0.5);
      }
      
      /* Success */
      .btn-success .shadow {
        background: #ff9900;
      }
      
      .btn-success .front {
        background: linear-gradient(180deg, #ffcd40 0%, #ff9900 100%);
        color: #000000;
        box-shadow: 0 0 15px rgba(255, 205, 0, 0.5);
      }
      
      /* Disabled state */
      .btn-disabled .front,
      .btn-disabled .shadow {
        box-shadow: none;
      }
    `
  };
};

/**
 * Helper function to inject button styles into the document head
 * @param {Object} options - Options for the injection
 * @param {string} [options.id='buzztime-button-styles'] - ID for the style element
 */
export const injectButtonStyles = (options = {}) => {
  const { id = 'buzztime-button-styles' } = options;
  
  // For use in React components
  useEffect(() => {
    // Check if styles are already injected
    let styleEl = document.getElementById(id);
    
    // If not, create and inject
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = id;
      styleEl.innerHTML = createStyledButton({}).cssString;
      document.head.appendChild(styleEl);
    }
    
    // No cleanup needed as we want styles to persist
    return () => {};
  }, [id]);
};
